import {
  Checkbox,
  CircleLoader,
  Divider,
  Form,
  Header,
  IconCheck,
  IconX,
  PrimaryButton,
  SecondaryButton,
  Space,
  TertiaryButton,
  Text,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import { type MessageDescriptor } from 'react-intl'

import { useChecklistManager } from '@webapp/core/hooks/useChecklistManager'
import { useProcurifyIntl } from '@webapp/hooks'
import { CheckboxGridStyled } from './styles/CheckboxGridStyled'

export interface CatalogAccessGridMessages {
  header: MessageDescriptor
  selectAll: MessageDescriptor
  deselectAll: MessageDescriptor
  save: MessageDescriptor
  cancel: MessageDescriptor
  emptyState: MessageDescriptor
}

interface ICatalogAccessGridFormProps<T extends { id: number }> {
  options: T[]
  grantedIds: number[]
  getOptionLabel: (option: T) => string
  columns: number
  isSaving: boolean
  onSave: (selectedIds: number[]) => void
  onCancel?: () => void
  messages: CatalogAccessGridMessages
}

const CatalogAccessGridForm = <T extends { id: number }>({
  options,
  grantedIds,
  getOptionLabel,
  columns,
  isSaving,
  onSave,
  onCancel,
  messages,
}: ICatalogAccessGridFormProps<T>) => {
  const intl = useProcurifyIntl()

  const granted = options.filter((option) => grantedIds.includes(option.id))

  const { checklist, check, checklistState } = useChecklistManager<T>({
    keyId: 'id',
    initialList: granted,
  })

  // Formik is only here for the DS `Form` submit plumbing — the real selection
  // source of truth is `useChecklistManager` (`checklist`), so there are no form
  // fields/validation to model.
  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      onSave(checklist.map((option) => option.id))
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate>
        <Space>
          <Header as='h5'>{intl.formatMessage(messages.header)}</Header>

          <Space direction='horizontal' justify='space-between' align='center'>
            <Space direction='horizontal'>
              <SecondaryButton
                type='button'
                iconComponent={<IconCheck />}
                onClick={() => check(options, true)}
                disabled={isSaving}
              >
                {intl.formatMessage(messages.selectAll)}
              </SecondaryButton>
              <SecondaryButton
                type='button'
                iconComponent={<IconX />}
                onClick={() => check(options, false)}
                disabled={isSaving}
              >
                {intl.formatMessage(messages.deselectAll)}
              </SecondaryButton>
            </Space>
          </Space>

          <Divider />

          <CheckboxGridStyled $columns={columns}>
            {options.map((option) => (
              <Checkbox
                key={option.id}
                checked={!!checklistState.items[option.id]}
                onChange={() => check(option)}
                disabled={isSaving}
                inputProps={{ 'aria-label': getOptionLabel(option) }}
              >
                {getOptionLabel(option)}
              </Checkbox>
            ))}
          </CheckboxGridStyled>

          <Divider />

          <Space direction='horizontal' justify='end' align='center'>
            {onCancel && (
              <TertiaryButton
                type='button'
                onClick={onCancel}
                disabled={isSaving}
              >
                {intl.formatMessage(messages.cancel)}
              </TertiaryButton>
            )}
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.save)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

export interface ICatalogAccessGridProps<T extends { id: number }> {
  options: T[]
  grantedIds: number[]
  getOptionLabel: (option: T) => string
  /** Remounts the form (resets the checklist) when the selected target changes. */
  resetKey: string | number
  columns?: number
  isLoading: boolean
  isSaving: boolean
  onSave: (selectedIds: number[]) => void
  onCancel?: () => void
  messages: CatalogAccessGridMessages
}

/**
 * Shared checkbox-grid used by the catalog-permission assignment features
 * (assign users → secondary-category / assign secondary-categories → user):
 * select-all / deselect-all, per-option checkboxes, save/cancel. Feature views
 * supply the option type, label getter, granted ids, and their i18n messages.
 */
export const CatalogAccessGrid = <T extends { id: number }>({
  options,
  grantedIds,
  getOptionLabel,
  resetKey,
  columns = 3,
  isLoading,
  isSaving,
  onSave,
  onCancel,
  messages,
}: ICatalogAccessGridProps<T>) => {
  const intl = useProcurifyIntl()

  if (isLoading) {
    return (
      <Space align='center'>
        <CircleLoader />
      </Space>
    )
  }

  if (options.length === 0) {
    return (
      <Space align='center'>
        <Text>{intl.formatMessage(messages.emptyState)}</Text>
      </Space>
    )
  }

  return (
    <CatalogAccessGridForm
      key={resetKey}
      options={options}
      grantedIds={grantedIds}
      getOptionLabel={getOptionLabel}
      columns={columns}
      isSaving={isSaving}
      onSave={onSave}
      onCancel={onCancel}
      messages={messages}
    />
  )
}
